import React from "react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import useTranslation from "@/hooks/useTranslation";

const Privacy = () => {
  const { t, language } = useTranslation();

  return (
    <div className="min-h-screen py-16">
      <SEO 
        title={`${t('privacyPolicy')} - NuWav Laboratories`}
        description={`${t('privacyPolicy')} for NuWav Laboratories. Learn how we protect your personal information and health data as a HIPAA compliant laboratory.`}
        keywords="privacy policy, data protection, HIPAA compliance, medical data, health information privacy, NuWav Laboratories"
        canonicalUrl={language === 'es' ? "https://nuwavlabs.com/es/privacidad" : "https://nuwavlabs.com/privacy"}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl font-bold mb-8 text-[#000080]">{t('privacyPolicy')}</h1>
          
          <div className="prose prose-lg">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-[#000080]">{t('privacyIntro')}</h2>
              <p className="text-gray-700 mb-4">
                {t('privacyIntroText')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-[#000080]">{t('infoWeCollect')}</h2>
              <h3 className="text-xl font-semibold mb-3">{t('personalHealthInfo')}</h3>
              <p className="text-gray-700 mb-4">
                {t('personalHealthInfoText')}
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>{t('patientDemographic')}</li>
                <li>{t('medicalHistory')}</li>
                <li>{t('labResults')}</li>
                <li>{t('insuranceInfo')}</li>
                <li>{t('otherHealthInfo')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-[#000080]">{t('howWeUseInfo')}</h2>
              <p className="text-gray-700 mb-4">
                {t('useInfoText')}
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>{t('providingLabServices')}</li>
                <li>{t('processingResults')}</li>
                <li>{t('billingPayment')}</li>
                <li>{t('communicatingTests')}</li>
                <li>{t('qualityAssurance')}</li>
                <li>{t('complianceRequirements')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-[#000080]">{t('dataProtection')}</h2>
              <p className="text-gray-700 mb-4">
                {t('dataProtectionText')}
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>{t('encryption')}</li>
                <li>{t('accessControls')}</li>
                <li>{t('securityAssessments')}</li>
                <li>{t('staffTraining')}</li>
                <li>{t('physicalSecurity')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-[#000080]">{t('hipaaCompliance')}</h2>
              <p className="text-gray-700 mb-4">
                {t('hipaaComplianceText')}
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>{t('maintainingPrivacy')}</li>
                <li>{t('providingAccess')}</li>
                <li>{t('obtainingAuth')}</li>
                <li>{t('accountingDisclosures')}</li>
                <li>{t('privacyPractices')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-[#000080]">{t('yourRights')}</h2>
              <p className="text-gray-700 mb-4">
                {t('yourRightsText')}
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>{t('rightToAccess')}</li>
                <li>{t('rightToCorrections')}</li>
                <li>{t('rightToAccounting')}</li>
                <li>{t('rightToRestrictions')}</li>
                <li>{t('rightToConfidential')}</li>
                <li>{t('rightToNotice')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-[#000080]">{t('contactInfo')}</h2>
              <p className="text-gray-700 mb-4">
                {t('contactInfoText')}
              </p>
              <address className="text-gray-700 not-italic">
                NuWav Laboratories<br />
                2015 Midway Rd Bldg 111<br />
                Carrollton, TX 75006
              </address>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-[#000080]">{t('updatesToPolicy')}</h2>
              <p className="text-gray-700 mb-4">
                {t('updatesToPolicyText')}
              </p>
            </section>

            <div className="text-gray-600 mt-8">
              {t('lastUpdated')}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Privacy;
